import { graphqlClient } from './graphqlClient';
import type { Workspace } from '../lib/types';

/**
 * Workspace Service
 * Handles all CRUD operations for workspace management using direct GraphQL client
 */
export class WorkspaceService {
  /**
   * Create a new workspace
   */
  static async createWorkspace(input: {
    name: string;
    domain?: string;
    settings?: string;
  }): Promise<Workspace | null> {
    try {
      const mutation = `
        mutation CreateWorkspace($input: CreateWorkspaceInput!) {
          createWorkspace(input: $input) {
            id
            name
            domain
            settings
            createdAt
            updatedAt
          }
        }
      `;

      const data = await graphqlClient.mutate(mutation, {
        input: {
          name: input.name,
          ...(input.domain && input.domain.trim() && { domain: input.domain }),
          ...(input.settings && input.settings.trim() && { settings: input.settings }),
        },
      });

      return data.createWorkspace as Workspace;
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  }

  /**
   * Get all workspaces for the current user
   */
  static async listWorkspaces(): Promise<Workspace[]> {
    try {
      const query = `
        query ListWorkspaces {
          listWorkspaces {
            id
            name
            domain
            settings
            createdAt
            updatedAt
          }
        }
      `;

      const data = await graphqlClient.query(query);
      return data.listWorkspaces || [];
    } catch (error) {
      console.error('Error listing workspaces:', error);
      throw error;
    }
  }

  /**
   * Get a specific workspace by ID
   */
  static async getWorkspace(id: string): Promise<Workspace | null> {
    try {
      const query = `
        query GetWorkspace($id: ID!) {
          getWorkspace(id: $id) {
            id
            name
            domain
            settings
            createdAt
            updatedAt
          }
        }
      `;

      const data = await graphqlClient.query(query, { id });
      return data.getWorkspace as Workspace;
    } catch (error) {
      console.error('Error getting workspace:', error);
      throw error;
    }
  }

  /**
   * Update a workspace
   */
  static async updateWorkspace(
    id: string,
    input: {
      name?: string;
      domain?: string;
      settings?: string;
    }
  ): Promise<Workspace | null> {
    try {
      const mutation = `
        mutation UpdateWorkspace($input: UpdateWorkspaceInput!) {
          updateWorkspace(input: $input) {
            id
            name
            domain
            settings
            createdAt
            updatedAt
          }
        }
      `;

      const data = await graphqlClient.mutate(mutation, {
        input: {
          id,
          ...input,
        },
      });

      return data.updateWorkspace as Workspace;
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error;
    }
  }

  /**
   * Delete a workspace
   */
  static async deleteWorkspace(id: string): Promise<boolean> {
    try {
      const mutation = `
        mutation DeleteWorkspace($input: DeleteWorkspaceInput!) {
          deleteWorkspace(input: $input) {
            id
          }
        }
      `;

      await graphqlClient.mutate(mutation, {
        input: { id },
      });

      return true;
    } catch (error) {
      console.error('Error deleting workspace:', error);
      throw error;
    }
  }
}